import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";

// Reveal content that the pre-render hides until App's mount effect runs.
// If a render/hydration error is caught here, App's effect never ran, so the
// page would otherwise stay invisible (no-anim + #root opacity:0).
function revealContent() {
  const hide = document.getElementById("__snap-hide");
  if (hide && hide.parentNode) hide.parentNode.removeChild(hide);
  document.documentElement.classList.remove("no-anim");
}

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch() {
    revealContent();
  }
  render() {
    if (this.state.error) {
      const debug =
        typeof window !== "undefined" && /[?&]debug=1\b/.test(window.location.search);
      return (
        <div style={{ padding: "40px 20px", textAlign: "center", font: "16px/1.5 sans-serif" }}>
          <h2 style={{ color: "#5C005D" }}>Something went wrong</h2>
          <p>Please refresh the page or try again shortly.</p>
          {debug && (
            <pre
              style={{
                textAlign: "left",
                whiteSpace: "pre-wrap",
                font: "12px/1.45 monospace",
                color: "#b00020",
                marginTop: 24,
              }}
            >
              {this.state.error.stack || this.state.error.message || String(this.state.error)}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

const container = document.getElementById("root");

// react-snap pre-renders specific routes into their own index.html files.
// For routes without a pre-rendered file, Netlify's SPA fallback serves the
// homepage HTML — which has content that doesn't match the current route.
// __SNAP_ROUTE__ is set by the inline script in index.html *before* it
// rewrites the canonical for SEO. It holds the original pathname baked into
// the static HTML by react-snap, so we can detect content mismatches.
const prerenderedRoute = window.__SNAP_ROUTE__ || null;
const currentPath = window.location.pathname;

// Only hydrate if the pre-rendered HTML was built with the current JS bundle.
// App.js renders a hidden <span id="__build_v3"> as its first child.
// If it's absent, the pre-rendered HTML is stale (from a previous build that
// didn't include this marker), so we fall back to a fresh client render to
// avoid React error #418 (hydration mismatch).
const BUILD_VERSION = "__build_v3";
const prerenderedVersion = container.querySelector(`#${BUILD_VERSION}`);

const shouldHydrate =
  container.hasChildNodes() &&
  prerenderedRoute === currentPath &&
  prerenderedVersion !== null;

if (shouldHydrate) {
  ReactDOM.hydrateRoot(
    container,
    <React.StrictMode>
      <AppErrorBoundary>
        <App />
      </AppErrorBoundary>
    </React.StrictMode>
  );
} else {
  // Clear stale pre-rendered HTML that belongs to a different route
  if (container.hasChildNodes()) {
    container.textContent = '';
  }
  const root = ReactDOM.createRoot(container);
  root.render(
    <React.StrictMode>
      <AppErrorBoundary>
        <App />
      </AppErrorBoundary>
    </React.StrictMode>
  );
}

reportWebVitals();
